import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/admin/ui";

function TaskCard({ task, onStart, onSubmit }) {
  const canStart = ["assigned", "accepted"].includes(task.status);
  const canSubmit = ["in_progress", "accepted"].includes(task.status);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`worker-task-${task.task_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-[#0A1128]">{task.title}</div>
          <div className="text-xs text-slate-400 mt-0.5 font-mono-metric">{task.task_id} · {task.priority}</div>
        </div>
        <StatusBadge status={task.status} />
      </div>
      {task.description && <p className="text-sm text-slate-500 mt-2">{task.description}</p>}
      {task.customer && <p className="text-xs text-slate-400 mt-1">Customer: {task.customer}</p>}
      {task.due_date && <p className="text-xs text-slate-400 mt-1">Due: {task.due_date.slice(0, 10)}</p>}
      <div className="flex gap-2 mt-3">
        {canStart && <button onClick={() => onStart(task)} data-testid={`start-${task.task_id}`} className="flex-1 py-2.5 rounded-xl bg-[#0A1128] text-white font-semibold text-sm">Start Task</button>}
        {canSubmit && <button onClick={() => onSubmit(task)} data-testid={`submit-${task.task_id}`} className="flex-1 py-2.5 rounded-xl bg-[#1D4ED8] text-white font-semibold text-sm">Submit Task</button>}
        {["submitted", "under_review"].includes(task.status) && <div className="flex-1 py-2.5 rounded-xl bg-amber-50 text-amber-600 font-semibold text-sm text-center">Under review</div>}
        {["approved", "completed"].includes(task.status) && <div className="flex-1 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 font-semibold text-sm text-center">✓ Completed</div>}
        {task.status === "rejected" && <button onClick={() => onSubmit(task)} className="flex-1 py-2.5 rounded-xl bg-rose-50 text-rose-600 font-semibold text-sm">Resubmit</button>}
      </div>
    </div>
  );
}

export default function WorkerTasks() {
  const [tasks, setTasks] = useState([]);
  const [submitFor, setSubmitFor] = useState(null);
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = useCallback(() => { api.get("/tasks?mine=true&limit=50").then((r) => setTasks(r.data.items)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (task) => {
    try { await api.post(`/tasks/${task.task_id}/start`); toast.success("Task started"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doSubmit = async (e) => {
    e.preventDefault();
    try { await api.post(`/tasks/${submitFor.task_id}/submit`, { notes, outcome }); toast.success("Submitted for review"); setSubmitFor(null); setNotes(""); setOutcome(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-head text-2xl font-extrabold text-[#0A1128]">My Tasks</h1>
      {tasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
          <p className="font-semibold text-[#0A1128]">No tasks yet</p>
          <p className="text-sm text-slate-500 mt-1">New tasks assigned to you will appear here.</p>
        </div>
      ) : tasks.map((t) => <TaskCard key={t.id} task={t} onStart={start} onSubmit={setSubmitFor} />)}

      {submitFor && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50" onClick={() => setSubmitFor(null)}>
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl sm:rounded-3xl p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-xl font-bold text-[#0A1128] mb-4">Submit · {submitFor.title}</h3>
            <form onSubmit={doSubmit} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-600">Notes</label>
                <textarea data-testid="submit-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1D4ED8] outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Outcome</label>
                <input data-testid="submit-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1D4ED8] outline-none" />
              </div>
              <button data-testid="submit-confirm" className="w-full py-3.5 rounded-xl bg-[#1D4ED8] text-white font-semibold text-lg">Submit for Review</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
